/* eslint-disable @atlaskit/design-system/use-spotlight-package */
import { Pulse as SpotlightPulse } from '@atlaskit/onboarding/target';
import SpotlightTransition from '@atlaskit/onboarding/spotlight-transition';
/* eslint-enable @atlaskit/design-system/use-spotlight-package */
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
// @ts-expect-error: 'React' is declared but its value is never read
// React import required to render in Jira SSR
import React, { type PropsWithChildren, useCallback, useState } from 'react';

import { IntroductionStep } from './introduction-step';

import { ParentStep } from './parent-step';

import type { SpotlightTourStep } from './types';

import { type JSX } from 'react/jsx-runtime';

const STEPS: SpotlightTourStep[] = [IntroductionStep, ParentStep];

export const FilterRefinementSpotlightTour = ({ children }: PropsWithChildren): JSX.Element => {
	const [step, setStep] = useState<number | null>(0);

	const { onClosed, onAcknowledged } = usePostOfficeUserInteractions();

	const onDismiss = useCallback(() => {
		// The final step's "OK" control also calls this: report it as the finish control of the
		// tour (onAcknowledged) rather than an explicit close (onClosed).
		const isFinalStep = step !== null && step === STEPS.length - 1;

		setStep(null);

		// Reports the interaction so the message is not displayed again to the user
		// @see https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/system-filters/#dismissal-check-filter
		if (isFinalStep) {
			void onAcknowledged({ interactionIdentifier: 'done', additionalMetadata: { step } });
		} else {
			void onClosed({ interactionIdentifier: 'dismiss', additionalMetadata: { step } });
		}
	}, [onAcknowledged, onClosed, step]);

	const onNext = useCallback(() => {
		if (step !== null && step < STEPS.length - 1) {
			setStep(step + 1);
		} else {
			onDismiss();
		}
	}, [onDismiss, step]);

	const StepComponent = step !== null ? STEPS[step] : null;

	return (
		<>
			<SpotlightPulse radius={3} pulse={step === 0}>
				{children}
			</SpotlightPulse>
			{StepComponent && (
				<SpotlightTransition>
					<StepComponent onDismiss={onDismiss} onNext={onNext} />
				</SpotlightTransition>
			)}
		</>
	);
};
